import React, { useRef, useState, useEffect } from "react";
import { Crosshair } from "lucide-react";

export const focusPos = (f) => `${f?.x ?? 50}% ${f?.y ?? 50}%`;

function Preview({ src, focus, label, ratio, testid }) {
  return (
    <div className="flex flex-col items-center gap-1">
      <div className={`${ratio} rounded-lg overflow-hidden bg-black border border-[var(--line)]`} data-testid={testid}>
        <img src={src} alt="" className="w-full h-full object-cover" style={{ objectPosition: focusPos(focus) }} />
      </div>
      <span className="text-[10px] uppercase tracking-[0.16em] text-[var(--muted)]">{label}</span>
    </div>
  );
}

// Tap or drag on the full photo to pick the part that must stay visible when the banner crops it.
export function FocusPicker({ src, focus, onChange, testid = "focus-picker" }) {
  const box = useRef(null);
  const [ready, setReady] = useState(false);
  const [live, setLive] = useState(focus || { x: 50, y: 50 });
  const drag = useRef(false);
  useEffect(() => { if (!drag.current) setLive(focus || { x: 50, y: 50 }); }, [focus?.x, focus?.y]); // eslint-disable-line
  useEffect(() => { setReady(false); }, [src]);
  const at = (e) => {
    const r = box.current.getBoundingClientRect();
    return { x: Math.round(Math.min(100, Math.max(0, ((e.clientX - r.left) / r.width) * 100))), y: Math.round(Math.min(100, Math.max(0, ((e.clientY - r.top) / r.height) * 100))) };
  };
  const down = (e) => { if (!ready) return; drag.current = true; box.current.setPointerCapture?.(e.pointerId); setLive(at(e)); };
  const move = (e) => { if (drag.current) setLive(at(e)); };
  const up = (e) => { if (!drag.current) return; drag.current = false; const f = at(e); setLive(f); onChange(f); };
  return (
    <div className="space-y-3" data-testid={testid}>
      <p className="text-xs text-[var(--muted)] flex items-center gap-1.5"><Crosshair className="w-3.5 h-3.5 text-[var(--gold)]" /> Tap or drag to the faces or the part that must always show.</p>
      <div className="flex flex-wrap items-end gap-4">
        <div ref={box} onPointerDown={down} onPointerMove={move} onPointerUp={up} className="relative inline-block min-w-[120px] min-h-[80px] bg-black/5 rounded-lg cursor-crosshair select-none touch-none max-w-full" data-testid={`${testid}-area`}>
          <img src={src} alt="" draggable="false" onLoad={() => setReady(true)} className="block max-h-64 max-w-full rounded-lg" />
          {ready && <span className="absolute w-6 h-6 -ml-3 -mt-3 rounded-full border-2 border-white bg-[var(--gold)]/80 ring-2 ring-black/50 shadow-[0_0_0_4px_rgba(0,0,0,0.25)] pointer-events-none" style={{ left: `${live.x}%`, top: `${live.y}%` }} data-testid={`${testid}-dot`} />}
        </div>
        <Preview src={src} focus={live} label="Computer" ratio="w-40 aspect-[16/9]" testid={`${testid}-preview-wide`} />
        <Preview src={src} focus={live} label="Phone" ratio="w-20 aspect-[9/16]" testid={`${testid}-preview-phone`} />
      </div>
    </div>
  );
}
